import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const api = useApi();
  const [profile, setProfile] = useState(user);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/users/profile', profile);
      setMessage('Profile updated successfully.');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error updating profile.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-transparent border-b border-white/20 text-white px-0 py-3 text-sm font-light transition-colors duration-300 focus:outline-none focus:border-gold placeholder:text-white/20 tracking-wide";
  const labelClass = "block text-[10px] tracking-widest uppercase text-textMuted mb-2 flex items-center gap-4";

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen bg-deepDark py-16 px-4"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-12 border-b border-white/10 pb-6 flex justify-between items-baseline">
          <h1 className="text-4xl font-serif text-white tracking-wide">Client Profile</h1>
          <button onClick={logout} className="text-xs text-textMuted tracking-widest uppercase hover:text-gold transition-colors">Sign Out</button>
        </div>

        {message && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className={`p-4 mb-8 text-xs tracking-widest uppercase text-center border ${message.includes('successfully') ? 'text-gold border-gold/30 bg-gold/5' : 'text-red-500 border-red-500/30 bg-red-500/5'}`}
          >
            {message}
          </motion.div>
        )}

        <div className="space-y-12">
          {/* Identity */}
          <section className="bg-surfaceDark border border-white/5 p-8 md:p-10">
            <h2 className="text-xl font-serif text-white mb-8 tracking-wide">Identity</h2>
            <form onSubmit={handleSaveProfile} className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className={labelClass}>First Name</label>
                  <input type="text" name="firstName" value={profile?.firstName || ''} onChange={handleChange} className={inputClass} required />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input type="text" name="lastName" value={profile?.lastName || ''} onChange={handleChange} className={inputClass} required />
                </div>
              </div>

              <div>
                <label className={labelClass}>Registered Email <span className="text-[8px] text-white/30 border border-white/10 px-2 py-0.5 ml-auto">Immutable</span></label>
                <input type="email" value={profile?.email || ''} disabled className="w-full bg-transparent border-b border-white/10 text-white/50 px-0 py-3 text-sm font-light cursor-not-allowed tracking-wide" />
              </div>

              <div>
                <label className={labelClass}>Contact Number</label>
                <input type="tel" name="phone" value={profile?.phone || ''} onChange={handleChange} className={inputClass} />
              </div>

              <div className="pt-4">
                <button type="submit" disabled={saving} className="w-full sm:w-auto bg-gold text-deepDark px-10 py-4 text-xs font-bold tracking-widest uppercase hover:bg-white transition-colors disabled:opacity-50">
                  {saving ? 'Updating...' : 'Save Preferences'}
                </button>
              </div>
            </form>
          </section>

          {/* Addresses */}
          <section className="bg-surfaceDark border border-white/5 p-8 md:p-10">
            <div className="flex justify-between items-baseline mb-8">
              <h2 className="text-xl font-serif text-white tracking-wide">Directory</h2>
              <span className="text-[10px] text-textMuted tracking-widest uppercase">Saved Locations</span>
            </div>
            
            <div className="space-y-4">
              {profile?.addresses?.length > 0 ? profile.addresses.map((address, index) => (
                <div key={index} className="p-6 border border-white/10 relative group hover:border-gold/50 transition-colors">
                  {address.isDefault && <span className="absolute top-6 right-6 text-[8px] text-gold tracking-widest uppercase border border-gold/30 px-2 py-1">Primary</span>}
                  <p className="text-white font-light text-sm tracking-wide mb-1 pr-20">{address.street}</p>
                  <p className="text-textMuted font-light text-sm">{address.city}, {address.state} {address.zipCode}</p>
                </div>
              )) : (
                <p className="text-textMuted text-sm font-light py-4">No locations registered.</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </motion.div>
  );
};

export default ProfilePage;
